import type { BackgroundJobFieldsFragment } from "~/gql/queries/backgroundJob.generated";

import { BackgroundJobType } from "~/gql/graphqlBase.generated";

export const OPEN_JOB_STATUSES = ["QUEUED", "PENDING", "RUNNING"] as const;
export const FINISHED_JOB_STATUSES = ["COMPLETED", "COMPLETED_WITH_ERRORS", "CANCELLED", "FAILED"] as const;

const OPEN = new Set<string>(OPEN_JOB_STATUSES);
const FINISHED = new Set<string>(FINISHED_JOB_STATUSES);

export interface QueueJob {
	dismissedAt?: null | string;
	done: number;
	failed: number;
	finishedAt?: null | string;
	isListedOnly: boolean;
	jobId: string;
	status: string;
	title: string;
	total: number;
	type: string;
	updatedAt: string;
}

export type QueueBadgeState = "done" | "failed" | "none" | "running" | "waiting";

export interface ProgressSample {
	at: number;
	done: number;
}

export type CountdownEstimate = "hidden" | "less-than-a-minute" | "queued" | { minutes: number };

const MINUTE_MS = 60_000;

export const isDismissed = (job: Pick<QueueJob, "dismissedAt">): boolean =>
	job.dismissedAt != null && job.dismissedAt !== "";

export const isOpenJob = (job: Pick<QueueJob, "status">): boolean => OPEN.has(job.status);

export const isFinishedJob = (job: Pick<QueueJob, "status">): boolean => FINISHED.has(job.status);

export const visibleJobs = <T extends Pick<QueueJob, "dismissedAt">>(jobs: readonly T[]): T[] =>
	jobs.filter((job) => !isDismissed(job));

export const queueBadge = (jobs: readonly QueueJob[]): { count: number; state: QueueBadgeState } => {
	const visible = visibleJobs(jobs);
	if (visible.length === 0) return { count: 0, state: "none" };
	if (visible.some((job) => job.status === "RUNNING")) return { count: visible.length, state: "running" };
	if (visible.some((job) => job.failed > 0 || job.status === "FAILED" || job.status === "COMPLETED_WITH_ERRORS")) {
		return { count: visible.length, state: "failed" };
	}
	if (visible.every(isFinishedJob)) return { count: visible.length, state: "done" };
	return { count: visible.length, state: "waiting" };
};

const byNewest = (left: string, right: string): number => (left < right ? 1 : left > right ? -1 : 0);

export const orderedQueue = <T extends QueueJob>(jobs: readonly T[]): { finished: T[]; inProgress: T[] } => {
	const visible = visibleJobs(jobs);
	const inProgress = visible.filter(isOpenJob).sort((left, right) => byNewest(left.updatedAt, right.updatedAt));
	const finished = visible
		.filter((job) => !isOpenJob(job))
		.sort((left, right) => byNewest(left.finishedAt ?? left.updatedAt, right.finishedAt ?? right.updatedAt));
	return { finished, inProgress };
};

export const appendProgressSample = (
	samples: readonly ProgressSample[],
	done: number,
	at: number
): ProgressSample[] => {
	const last = samples[samples.length - 1];
	if (!last) return [{ at, done }];
	if (last.done === done) return samples as ProgressSample[];
	return [...samples, { at, done }];
};

export const estimateCountdown = (
	samples: readonly ProgressSample[],
	job: Pick<QueueJob, "isListedOnly" | "status" | "total">,
	latestDone: number
): CountdownEstimate => {
	if (job.status === "QUEUED") return "queued";
	if (!isOpenJob(job)) return "hidden";
	if (job.isListedOnly && job.total <= 0) return "hidden";
	if (samples.length < 2) return "hidden";
	const first = samples[0];
	const latest = samples[samples.length - 1];
	const elapsed = latest.at - first.at;
	const gained = latest.done - first.done;
	const left = job.total - latestDone;
	if (elapsed <= 0 || gained <= 0 || left <= 0) return "hidden";
	const msLeft = (left / gained) * elapsed;
	if (msLeft < MINUTE_MS) return "less-than-a-minute";
	return { minutes: Math.ceil(msLeft / MINUTE_MS) };
};

export interface FinishNotice {
	key: "backgroundJobs.finishedLots" | "backgroundJobs.finishedOk" | "backgroundJobs.finishedPartial";
	values: { count?: number; created?: number; failed?: number };
}

export const BULK_DELETE_JOB_PARAM = "bulkDeleteJobId";

export type ListedJobLink = Pick<
	BackgroundJobFieldsFragment,
	"isListedOnly" | "jobId" | "resultLink" | "sourceRoute" | "type"
>;

export const bulkDeleteResultsRoute = (jobId: string): string =>
	`/fulfill/orders?${BULK_DELETE_JOB_PARAM}=${encodeURIComponent(jobId)}`;

export const withoutBulkDeleteJobParam = (params: URLSearchParams): URLSearchParams => {
	if (!params.has(BULK_DELETE_JOB_PARAM)) return params;
	const next = new URLSearchParams(params);
	next.delete(BULK_DELETE_JOB_PARAM);
	return next;
};

export const linkedBulkDeleteHandledKey = (navigationKey: string, tenantId: string, jobId: string): string =>
	`${navigationKey}:${tenantId}:${jobId}`;

export type LinkedBulkDeleteDecision = "apply" | "skip" | "wait";

export const linkedBulkDeleteDecision = (input: {
	linkedJobId: string;
	lookupSettled: boolean;
	mastersPending: boolean;
	progressReady: boolean;
	trackedFinished: boolean;
	trackedJobGone: boolean;
	trackedJobId: null | string;
}): LinkedBulkDeleteDecision => {
	if (!input.lookupSettled && !input.trackedJobId) return "wait";
	if (!input.trackedJobId || input.trackedJobId === input.linkedJobId) return "apply";
	if (input.mastersPending) return "skip";
	if (!input.progressReady && !input.trackedJobGone) return "wait";
	if (input.trackedFinished || input.trackedJobGone) return "apply";
	return "skip";
};

export const scheduleImportJobRoute = (jobId: string): string =>
	`/produce/production/schedule/import/${encodeURIComponent(jobId)}`;

export const listedJobDestination = (job: ListedJobLink): null | string => {
	if (job.resultLink) return job.resultLink;
	if (job.type === BackgroundJobType.BulkOrderDelete) return bulkDeleteResultsRoute(job.jobId);
	if (job.type === BackgroundJobType.ScheduleImport) return scheduleImportJobRoute(job.jobId);
	if (job.isListedOnly && job.sourceRoute) return job.sourceRoute;
	return null;
};

export type VisibleBulkDeleteJob = Pick<BackgroundJobFieldsFragment, "jobId" | "type">;

export const visibleBulkDeleteJobId = (
	requestedJobId: null | string,
	pending: boolean,
	job: null | undefined | VisibleBulkDeleteJob
): null | string => {
	if (!requestedJobId || pending) return null;
	if (!job || job.jobId !== requestedJobId || job.type !== BackgroundJobType.BulkOrderDelete) return null;
	return requestedJobId;
};

type OpenBackgroundJobQueueListener = () => void;

const openQueueListeners = new Set<OpenBackgroundJobQueueListener>();

export const requestOpenBackgroundJobQueue = (): void => {
	for (const listener of openQueueListeners) listener();
};

export const subscribeOpenBackgroundJobQueue = (listener: OpenBackgroundJobQueueListener): (() => void) => {
	openQueueListeners.add(listener);
	return () => {
		openQueueListeners.delete(listener);
	};
};

export const finishNotice = (job: Pick<QueueJob, "done" | "failed" | "type">): FinishNotice => {
	if (job.failed > 0) {
		return { key: "backgroundJobs.finishedPartial", values: { created: job.done, failed: job.failed } };
	}
	if (job.type === "CREATE_PLAN") {
		return { key: "backgroundJobs.finishedLots", values: { count: job.done } };
	}
	return { key: "backgroundJobs.finishedOk", values: { count: job.done } };
};
